import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { toast } from "@volca/ui/lib/toast";
import { canManageReport } from "@/profitability/access";
import { CalendarDialog } from "@/profitability/calendar/calendar-line";
import {
  useReportCalendar,
  useUpdateCalendar,
} from "@/profitability/calendar/query";
import { describeCalendar } from "@/profitability/calendar/types";
import type { ProfitabilityReport } from "@/profitability/types";
import { reportError } from "@/profitability/utils";
import { SettingsGroup, SettingsRow } from "./settings-group";
import { SettingsLoadError } from "./settings-load-error";

export const PeriodGroup = ({
  businessId,
  report,
}: {
  businessId: string;
  report: ProfitabilityReport;
}) => {
  const calendar = useReportCalendar(businessId, report.id);
  const update = useUpdateCalendar(businessId, report.id);
  const [editing, setEditing] = useState(false);
  const data = calendar.data;
  return (
    <SettingsGroup title="Report dates">
      {calendar.isError ? (
        <SettingsLoadError
          label="Report dates could not load."
          retry={() => void calendar.refetch()}
        />
      ) : (
        <SettingsRow
          label="Default period"
          value={
            calendar.isPending ? (
              <Skeleton
                role="status"
                aria-label="Loading report dates"
                className="h-5 w-40"
              />
            ) : data ? (
              describeCalendar(data)
            ) : (
              "No default period configured"
            )
          }
          hint={`Suggests dates for new uploads. Reports run when you upload files.${data?.timezone && !data.timezone_is_business_default ? ` Dates use ${data.timezone}.` : ""}`}
          action={
            canManageReport(report) &&
            data && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
              >
                Edit
              </Button>
            )
          }
        />
      )}
      {editing && data && (
        <CalendarDialog
          open
          onOpenChange={setEditing}
          calendar={data}
          pending={update.isPending}
          onSave={(next) =>
            update.mutate(next, {
              onSuccess: () => setEditing(false),
              onError: (error) =>
                toast.error("Report dates were not saved. Try again.", {
                  description: reportError(error),
                }),
            })
          }
        />
      )}
    </SettingsGroup>
  );
};
